/**
 * =============================================================================
 * QUESTION FORM COMPONENT
 * =============================================================================
 *
 * Form component for submitting questions on the storefront.
 * Includes field validation, image uploads, and progress feedback.
 *
 * ARCHITECTURE REFERENCE: PHASE_0B_SPEC.md §2.5.4
 * PATTERN REFERENCE: PHASE_0B_PATTERNS.md §7
 *
 * Features:
 * - Field validation (title, description, name, email)
 * - ImageUploader integration for attaching photos
 * - Sequential image upload with progress indication
 * - Best-effort client-side image downscale/re-encode before upload (PR5a)
 * - Success/error state handling
 * - Accessible form controls
 *
 * =============================================================================
 */

import React, { useState, useCallback, useId, useEffect, useLayoutEffect, useRef } from 'react';
import { CONTENT_VALIDATION, WINDOW_TYPE_OPTIONS } from '@find-my-part/types';
import type { WindowType } from '@find-my-part/types';
import { widgetApi, buildManageUrl } from '../api/client';
import { invalidatePublicReads } from '../api/publicQuestionCache';
import { ImageUploader } from './ImageUploader';
import { ImageUploadFailureNotice } from './ImageUploadFailureNotice';
import { OfflineNotice } from './OfflineNotice';
import { TurnstileWidget, type TurnstileSize, type TurnstileWidgetRef } from './TurnstileWidget';
import { SimilarQuestions } from './SimilarQuestions';
import { useStyleInjection, useOnlineStatus, useSimilarQuestions } from '../hooks';
import { logger } from '../lib/logger';
import { BREAKPOINTS } from '../tokens';
import {
  uploadOneImage,
  runImageUploadSequence,
  buildPartialUploadMessage,
  notifyHostCallback,
  type FailedImageUpload,
} from '../lib/imageUpload';
import {
  consumeFmpHistoryDraft,
  createFmpDraftNonce,
  persistFmpHistoryDraft,
  retireFmpHistoryDraft,
  type FmpSessionStorage,
} from '../lib/historyDraft';

// =============================================================================
// TYPES
// =============================================================================

function getDraftSessionStorage(): FmpSessionStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export interface QuestionFormProps {
  /**
   * Callback when the question is fully submitted — the question was created
   * AND all selected images uploaded (or the user chose to continue without the
   * photos that failed). NOT called while photos are still in a failed state
   * (BL-WIDGET-UPLOAD-FAILURE-UX-001): a partial upload must not read as success.
   */
  onSuccess?: (questionId: string, slug: string) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Additional CSS class name */
  className?: string;
}

interface FormData {
  title: string;
  description: string;
  posterName: string;
  posterEmail: string;
  windowType: WindowType | ''; // P1-29: Window type selector
  consentToPublicDisplay: boolean; // BL-PRIVACY-CONSENT-CAPTURE-001
}

interface FormErrors {
  title?: string;
  description?: string;
  posterName?: string;
  posterEmail?: string;
  turnstile?: string;
  consentToPublicDisplay?: string; // BL-PRIVACY-CONSENT-CAPTURE-001
  general?: string;
}

type SubmitState =
  | { status: 'idle' }
  | { status: 'submitting'; step: 'question' | 'images'; current: number; total: number }
  | {
      status: 'success';
      questionId: string;
      message: string;
      /**
       * ADR-024 §10.4 opaque MANAGE handle. When present, the success screen
       * renders a "manage/delete your photos" magic-link built from it. Held in
       * memory only — never persisted, never logged; the token appears only
       * inside that link's href.
       */
      manageToken?: string;
    }
  /**
   * The question was created, but one or more images failed to upload. The
   * question is real and pending review; the failed photos are NOT attached.
   * A recoverable state — never the green success screen, never `onSuccess`.
   * (BL-WIDGET-UPLOAD-FAILURE-UX-001)
   */
  | {
      status: 'partial';
      questionId: string;
      slug: string;
      message: string;
      totalImages: number;
      failed: FailedImageUpload[];
      /** ADR-024 §10.4 MANAGE handle, carried so the eventual success screen can
       * still surface the manage link after a partial-failure recovery. (The
       * ADR-024 §11 upload-proof used by retry lives in `uploadProofRef`, off the
       * render surface — see the ref declaration.) */
      manageToken?: string;
    }
  | { status: 'error'; message: string };

/** Standard pending-review confirmation (hoisted — used on several success paths). */
const PENDING_REVIEW_MESSAGE = 'Your question has been submitted and is pending review.';
const TURNSTILE_FLEXIBLE_MIN_INLINE_SIZE_PX = 300;
const TURNSTILE_LAYOUT_CHANGE_MESSAGE =
  'Verification layout changed. Please complete the security check again.';
type AskTurnstileSize = Extract<TurnstileSize, 'compact' | 'flexible'>;

/**
 * Read the slot's CSS content-box inline size without transform distortion.
 *
 * Cloudflare's flexible challenge has a 300px minimum that resolves inside the
 * mount element. DOMRect is the transformed border box, so merchant padding,
 * borders, or ancestor scale could otherwise select flexible for a content box
 * that is actually narrower than 300px.
 */
function getContentBoxInlineSize(element: HTMLElement): number {
  const styles = getComputedStyle(element);
  const usedInlineSize = Number.parseFloat(styles.inlineSize);

  if (Number.isFinite(usedInlineSize)) {
    if (styles.boxSizing !== 'border-box') return Math.max(0, usedInlineSize);

    const chromeInlineSize = [
      styles.paddingInlineStart,
      styles.paddingInlineEnd,
      styles.borderInlineStartWidth,
      styles.borderInlineEndWidth,
    ].reduce((total, value) => total + (Number.parseFloat(value) || 0), 0);

    return Math.max(0, usedInlineSize - chromeInlineSize);
  }

  // Legacy fallback: clientWidth is transform-independent and excludes borders,
  // but includes padding. It is integer-rounded, so modern engines should take
  // the computed-inline-size path above.
  const paddingInlineSize =
    (Number.parseFloat(styles.paddingInlineStart) || 0) +
    (Number.parseFloat(styles.paddingInlineEnd) || 0);
  return Math.max(0, element.clientWidth - paddingInlineSize);
}

// =============================================================================
// CHARACTER COUNT HELPER
// =============================================================================

interface CharacterCountProps {
  current: number;
  min: number;
  max: number;
  id?: string;
}

/**
 * Character count display component.
 * Shows current/max characters with visual feedback for validation state.
 *
 * P1-9: Add Character Count Displays to Forms
 *
 * States:
 * - Under minimum: Shows warning with remaining needed
 * - Near limit (>90%): Shows warning color
 * - Over limit: Shows error color (shouldn't happen with maxLength)
 * - Normal: Shows neutral color
 */
function CharacterCount({ current, min, max, id }: CharacterCountProps): React.ReactElement {
  const remaining = max - current;
  const percentUsed = (current / max) * 100;

  // Determine visual state
  let stateClass = '';
  let statusText = `${current} / ${max}`;

  if (current < min) {
    stateClass = 'fmp-char-count--warning';
    const needed = min - current;
    statusText = `${current} / ${max} (${needed} more needed)`;
  } else if (remaining <= 0) {
    stateClass = 'fmp-char-count--error';
    statusText = `${current} / ${max} (limit reached)`;
  } else if (percentUsed >= 90) {
    stateClass = 'fmp-char-count--warning';
    statusText = `${current} / ${max} (${remaining} remaining)`;
  }

  return (
    <span id={id} className={`fmp-char-count ${stateClass}`} aria-live="polite" aria-atomic="true">
      {statusText}
    </span>
  );
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validate email format using a simple regex.
 * This is intentionally permissive - server does final validation.
 */
function isValidEmail(email: string): boolean {
  // Basic email pattern: something@something.something
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Validate all form fields.
 * @returns Object with field errors, or empty object if valid
 */
function validateForm(data: FormData): FormErrors {
  const errors: FormErrors = {};

  // Title validation
  const titleTrimmed = data.title.trim();
  if (!titleTrimmed) {
    errors.title = 'Title is required';
  } else if (titleTrimmed.length < CONTENT_VALIDATION.MIN_TITLE_LENGTH) {
    errors.title = `Title must be at least ${CONTENT_VALIDATION.MIN_TITLE_LENGTH} characters`;
  } else if (titleTrimmed.length > CONTENT_VALIDATION.MAX_TITLE_LENGTH) {
    errors.title = `Title must be less than ${CONTENT_VALIDATION.MAX_TITLE_LENGTH} characters`;
  }

  // Description validation
  const descriptionTrimmed = data.description.trim();
  if (!descriptionTrimmed) {
    errors.description = 'Description is required';
  } else if (descriptionTrimmed.length < CONTENT_VALIDATION.MIN_DESCRIPTION_LENGTH) {
    errors.description = `Description must be at least ${CONTENT_VALIDATION.MIN_DESCRIPTION_LENGTH} characters`;
  } else if (descriptionTrimmed.length > CONTENT_VALIDATION.MAX_DESCRIPTION_LENGTH) {
    errors.description = `Description must be less than ${CONTENT_VALIDATION.MAX_DESCRIPTION_LENGTH} characters`;
  }

  // Poster name validation
  const nameTrimmed = data.posterName.trim();
  if (!nameTrimmed) {
    errors.posterName = 'Your name is required';
  }

  // Email validation
  const emailTrimmed = data.posterEmail.trim();
  if (!emailTrimmed) {
    errors.posterEmail = 'Email address is required';
  } else if (!isValidEmail(emailTrimmed)) {
    errors.posterEmail = 'Please enter a valid email address';
  }

  // Public-display consent validation (BL-PRIVACY-CONSENT-CAPTURE-001)
  if (!data.consentToPublicDisplay) {
    errors.consentToPublicDisplay = 'Please confirm you agree to public display before submitting';
  }

  return errors;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Question submission form with image upload support.
 *
 * @example
 * ```tsx
 * <QuestionForm
 *   onSuccess={(id, slug) => navigate(`/question/${slug}`)}
 *   onError={(err) => console.error(err)}
 * />
 * ```
 */
export function QuestionForm({
  onSuccess,
  onError,
  className = '',
}: QuestionFormProps): React.ReactElement {
  // Form field state
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    posterName: '',
    posterEmail: '',
    windowType: '', // P1-29: Window type selector
    consentToPublicDisplay: false, // BL-PRIVACY-CONSENT-CAPTURE-001
  });

  // Validation errors
  const [errors, setErrors] = useState<FormErrors>({});

  // Selected images (managed by ImageUploader)
  const [images, setImages] = useState<File[]>([]);

  // Slice 2A photo guidance is deliberately ephemeral. It resets with this
  // component (including route-away/return) and is never persisted.
  const [isPhotoGuidanceVisible, setIsPhotoGuidanceVisible] = useState(true);
  const [photoGuidanceStatus, setPhotoGuidanceStatus] = useState('');

  // Submission state
  const [submitState, setSubmitState] = useState<SubmitState>({ status: 'idle' });

  // Retry-in-flight flag for the partial-failure state (BL-WIDGET-UPLOAD-FAILURE-UX-001)
  const [isRetrying, setIsRetrying] = useState(false);

  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileLoading, setTurnstileLoading] = useState(true);
  const [turnstileSize, setTurnstileSize] = useState<AskTurnstileSize>('compact');
  const [turnstileGeneration, setTurnstileGeneration] = useState(0);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);
  const turnstileGroupRef = useRef<HTMLDivElement>(null);
  const turnstileSlotRef = useRef<HTMLDivElement>(null);
  const turnstileSizeRef = useRef<AskTurnstileSize>('compact');
  const turnstileGenerationRef = useRef(0);
  const restoreTurnstileFocusRef = useRef(false);
  // Synchronous token authority for provider callbacks and ResizeObserver.
  // State still drives rendering, while this ref prevents a just-invalidated
  // token from being accepted by a submit handler that closed over older state.
  const turnstileTokenRef = useRef<string | null>(null);

  // ADR-024 §11 upload-proof handle: memory-only, held in a ref (not React
  // submitState) so it stays off the render surface / DevTools / error-boundary
  // snapshots — parity with the reply path (useReplySubmission). Set at submit,
  // read on retry, cleared on reset. (The MANAGE handle stays in submitState: it
  // is intentionally rendered into the success-screen link's href.)
  const uploadProofRef = useRef<string | undefined>(undefined);
  // Synchronous re-entry guard (parity with useReplySubmission): the disabled
  // submit button relies on a committed re-render, so a rapid / programmatic
  // double-submit could otherwise create duplicate questions before `submitting`
  // lands. Set once past validation, cleared when terminal.
  const submitInFlightRef = useRef(false);
  // Same guard for the retry leg — each image POST consumes one capability use,
  // so two same-tick retries would double-spend uses (isRetrying state alone
  // doesn't commit synchronously).
  const retryInFlightRef = useRef(false);
  const successRef = useRef<HTMLDivElement>(null);
  const draftNonceRef = useRef<string | null>(null);

  // Generate unique IDs for form fields (accessibility)
  const formId = useId();
  const titleId = `${formId}-title`;
  const descriptionId = `${formId}-description`;
  const nameId = `${formId}-name`;
  const emailId = `${formId}-email`;
  const windowTypeId = `${formId}-window-type`; // P1-29
  const consentId = `${formId}-consent`; // BL-PRIVACY-CONSENT-CAPTURE-001
  // Composite (non-native-control) field group labels. These wrap custom
  // widgets (ImageUploader, TurnstileWidget) that have no single labelable
  // form control, so the visible label is exposed via role="group" +
  // aria-labelledby instead of an orphan <label> (BL-MSPL-WIDGET-A11Y-LABEL-001).
  const imagesLabelId = `${formId}-images-label`;
  const turnstileLabelId = `${formId}-turnstile-label`;
  const turnstileErrorId = `${formId}-turnstile-error`;
  const photoGuidanceTitleId = `${formId}-photo-guidance-title`;
  const photoGuidanceContentId = `${formId}-photo-guidance-content`;

  // Inject styles once per unique ID, shared across all QuestionForm instances
  useStyleInjection(formStyles, 'fmp-question-form-styles');

  // Track online/offline status for error handling
  const isOnline = useOnlineStatus();
  const isTurnstileSurfaceRendered =
    submitState.status !== 'success' && submitState.status !== 'partial';

  // Cloudflare flexible widgets require at least 300px. Resolve against the
  // verification slot's content box (not the viewport or transformed border
  // box) so narrow or host-styled columns use compact while normal/wide Ask
  // layouts fill their available inline size.
  // The layout effect resolves the initial mode before paint; ResizeObserver
  // then covers host reflow, zoom and orientation without resize feedback from
  // the provider iframe itself.
  useLayoutEffect(() => {
    const slot = turnstileSlotRef.current;
    if (!slot) return;

    const updateTurnstileSize = (observedInlineSize?: number): void => {
      // Preserve fractional CSS pixels at the 300px boundary. ResizeObserver's
      // content box and the computed-style fallback are both independent of
      // transforms and exclude merchant padding/borders.
      const slotInlineSize = observedInlineSize ?? getContentBoxInlineSize(slot);
      const nextSize: AskTurnstileSize =
        slotInlineSize >= TURNSTILE_FLEXIBLE_MIN_INLINE_SIZE_PX ? 'flexible' : 'compact';
      if (nextSize === turnstileSizeRef.current) return;

      if (slot.contains(document.activeElement)) {
        restoreTurnstileFocusRef.current = true;
      }
      turnstileSizeRef.current = nextSize;
      setTurnstileSize(nextSize);
      turnstileGenerationRef.current += 1;
      setTurnstileGeneration(turnstileGenerationRef.current);

      // Changing provider size removes/re-renders the challenge. A token from
      // the old iframe must never remain submit-authoritative after that reset.
      // Only announce "again" when the customer had actually verified; an
      // untouched challenge can resize silently without a misleading error.
      const invalidatedToken = turnstileTokenRef.current;
      turnstileTokenRef.current = null;
      setTurnstileToken(null);
      if (invalidatedToken) {
        setErrors((previous) => ({
          ...previous,
          turnstile: TURNSTILE_LAYOUT_CHANGE_MESSAGE,
        }));
      }
    };

    updateTurnstileSize();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const slotEntry = entries.find((entry) => entry.target === slot);
        updateTurnstileSize(slotEntry?.contentRect.width);
      });
      observer.observe(slot);
      return () => observer.disconnect();
    }

    const handleWindowResize = (): void => updateTurnstileSize();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [isTurnstileSurfaceRendered]);

  // Similar questions detection (P1-19)
  // Automatically fetches similar questions as user types title (with 300ms debounce)
  const { questions: similarQuestions, isLoading: isSimilarLoading } = useSimilarQuestions(
    formData.title
  );

  // Fresh activation consumes an allow-listed, same-tenant draft exactly once.
  // On bfcache restoration the preserved live DOM wins; only the history copy
  // is removed so a later reload cannot replay stale form state.
  useEffect(() => {
    const tenantKey = widgetApi.getStoreHash();
    const storage = getDraftSessionStorage();
    const restored = consumeFmpHistoryDraft(window.history, tenantKey, Date.now(), storage);
    if (restored) {
      draftNonceRef.current = restored.draftNonce;
      setFormData((previous) => ({
        ...previous,
        title: restored.title,
        description: restored.body,
        windowType: restored.windowType ?? '',
      }));
    }

    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        const retiredNonce = draftNonceRef.current;
        if (!retireFmpHistoryDraft(window.history, storage, tenantKey, retiredNonce)) {
          logger.warn('HISTORY_DRAFT_RETIREMENT_FAILED', { boundary: 'bfcache' });
        }
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Match ReplyForm's proven success-transition pattern. The persistent status
  // surface receives programmatic focus after every terminal-success path.
  useEffect(() => {
    if (submitState.status === 'success') successRef.current?.focus();
  }, [submitState.status]);

  const handleDismissPhotoGuidance = useCallback(() => {
    setIsPhotoGuidanceVisible(false);
    setPhotoGuidanceStatus('Photo tips hidden. You can show them again at any time.');
  }, []);

  const handleShowPhotoGuidance = useCallback(() => {
    setIsPhotoGuidanceVisible(true);
    setPhotoGuidanceStatus('Photo tips shown.');
  }, []);

  /**
   * Fetch config to get Turnstile site key.
   */
  useEffect(() => {
    let mounted = true;

    const fetchConfig = async (): Promise<void> => {
      try {
        const response = await widgetApi.config.get();
        if (mounted && response.success) {
          setTurnstileSiteKey(response.data.turnstileSiteKey);
        }
      } catch (error) {
        logger.error('CONFIG_LOAD_FAILURE', error instanceof Error ? error : null);
      } finally {
        if (mounted) {
          setTurnstileLoading(false);
        }
      }
    };

    if (widgetApi.isInitialized()) {
      fetchConfig();
    } else {
      setTurnstileLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Handle Turnstile verification success.
   */
  const handleTurnstileVerify = useCallback(
    (token: string) => {
      if (turnstileGeneration !== turnstileGenerationRef.current) return;
      turnstileTokenRef.current = token;
      setTurnstileToken(token);
      // Clear any previous turnstile error
      setErrors((prev) => ({ ...prev, turnstile: undefined }));
    },
    [turnstileGeneration]
  );

  /**
   * Handle Turnstile error.
   */
  const handleTurnstileError = useCallback(
    (error: string) => {
      if (turnstileGeneration !== turnstileGenerationRef.current) return;
      turnstileTokenRef.current = null;
      setTurnstileToken(null);
      setErrors((prev) => ({ ...prev, turnstile: error }));
    },
    [turnstileGeneration]
  );

  /**
   * Handle Turnstile token expiration.
   */
  const handleTurnstileExpire = useCallback(() => {
    if (turnstileGeneration !== turnstileGenerationRef.current) return;
    turnstileTokenRef.current = null;
    setTurnstileToken(null);
    setErrors((prev) => ({
      ...prev,
      turnstile: 'Verification expired. Please complete the security check again.',
    }));
  }, [turnstileGeneration]);

  const handleTurnstileLoad = useCallback(() => {
    if (
      turnstileGeneration !== turnstileGenerationRef.current ||
      !restoreTurnstileFocusRef.current
    ) {
      return;
    }
    restoreTurnstileFocusRef.current = false;
    const activeElement = document.activeElement;
    const focusFellOutWithProvider =
      activeElement === document.body || activeElement === document.documentElement;
    if (focusFellOutWithProvider) {
      turnstileGroupRef.current?.focus({ preventScroll: true });
    }
  }, [turnstileGeneration]);

  /**
   * Handle input field changes.
   * Uses functional updates to avoid dependency on errors state.
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      if (
        draftNonceRef.current === null &&
        (name === 'title' || name === 'description' || name === 'windowType')
      ) {
        try {
          draftNonceRef.current = createFmpDraftNonce();
        } catch {
          // Unsupported host crypto disables draft persistence; navigation will
          // fail safe to a new tab rather than tear down an unsaved form.
          draftNonceRef.current = null;
        }
      }
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Clear field error and general error using functional update
      // This avoids stale closure issues and unnecessary callback recreation
      setErrors((prev) => {
        const hasFieldError = prev[name as keyof FormErrors];
        const hasGeneralError = prev.general;
        if (hasFieldError || hasGeneralError) {
          return {
            ...prev,
            [name]: undefined,
            general: undefined,
          };
        }
        return prev;
      });
    },
    [] // No dependencies needed - uses functional updates
  );

  const persistDraftBeforeNavigate = useCallback((): boolean => {
    const tenantKey = widgetApi.getStoreHash();
    const draftNonce = draftNonceRef.current;
    if (!tenantKey || !draftNonce) return false;

    return persistFmpHistoryDraft(window.history, {
      tenantKey,
      draftNonce,
      title: formData.title,
      body: formData.description,
      windowType: formData.windowType,
    });
  }, [formData.description, formData.title, formData.windowType]);

  /**
   * Handle the public-display consent checkbox (BL-PRIVACY-CONSENT-CAPTURE-001).
   * Separate from handleChange because a checkbox carries `checked`, not `value`.
   */
  const handleConsentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData((prev) => ({ ...prev, consentToPublicDisplay: checked }));
    setErrors((prev) =>
      prev.consentToPublicDisplay ? { ...prev, consentToPublicDisplay: undefined } : prev
    );
  }, []);

  /**
   * Handle form submission.
   * 1. Validate form fields
   * 2. Submit question to API
   * 3. Upload images sequentially
   * 4. Show success/error state
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate form
      const validationErrors = validateForm(formData);

      // Validate Turnstile token
      const verifiedTurnstileToken = turnstileTokenRef.current;
      if (!verifiedTurnstileToken) {
        validationErrors.turnstile = 'Please complete the security verification';
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      // Clear any previous errors
      setErrors({});

      // Check if widget is initialized
      if (!widgetApi.isInitialized()) {
        setSubmitState({
          status: 'error',
          message: 'Unable to submit: widget not properly configured',
        });
        notifyHostCallback(() => onError?.(new Error('Widget initialization failed')));
        return;
      }

      // Check if user is online before attempting submission
      if (!isOnline) {
        setSubmitState({
          status: 'error',
          message:
            'Unable to submit: you appear to be offline. Please check your connection and try again.',
        });
        notifyHostCallback(() => onError?.(new Error('Network unavailable')));
        return;
      }

      // Re-entry guard (set only past validation so a failed validation doesn't
      // wedge it): block a second submit while the first is in flight.
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;

      // Deferred so the terminal onSuccess is invoked AFTER the try/finally: a
      // throwing host callback must not be caught below and flip a fully-created,
      // fully-attached question to the red error screen (parity with the reply
      // hook's createdReplyId pattern; the retry/continue paths already do this).
      let onSuccessArgs: { questionId: string; slug: string } | null = null;

      try {
        // Calculate total steps: 1 (question) + number of images
        const totalSteps = images.length + 1;

        // Step 1: Submit question
        // current=1 shows progress immediately (not 0% which feels stuck)
        setSubmitState({
          status: 'submitting',
          step: 'question',
          current: 1,
          total: totalSteps,
        });

        const questionResponse = await widgetApi.questions.submit({
          title: formData.title.trim(),
          description: formData.description.trim(),
          posterName: formData.posterName.trim(),
          posterEmail: formData.posterEmail.trim(),
          turnstileToken: verifiedTurnstileToken!,
          windowType: formData.windowType || undefined, // P1-29: Window type (optional)
          consentToPublicDisplay: formData.consentToPublicDisplay, // BL-PRIVACY-CONSENT-CAPTURE-001
        });

        // Turnstile tokens are single-use. Once a submission attempt reaches
        // the API, the token is no longer authoritative even when the API
        // rejects the question. The disabled provider instance was removed
        // during submission and will return unchecked if this form remains.
        turnstileTokenRef.current = null;
        setTurnstileToken(null);

        if (!questionResponse.success) {
          throw new Error(questionResponse.error.message);
        }

        // ADR-028 Slice 3: the question now durably exists, so retained public
        // browse/detail state is stale and every read still in flight is fenced
        // against repopulating it. This sits at the CREATION boundary, before
        // the optional image uploads below, because a partial or total photo
        // failure does not undo the created record. A FAILED create throws
        // above and never reaches this line. Invalidation only DROPS cached
        // state — it never inserts the new question, which is pending
        // moderation and must not be represented as published.
        invalidatePublicReads();

        // Creation is the durable submission boundary. Clear only after the
        // Worker accepted the question so a network/rejection failure cannot
        // destroy the customer's recoverable draft. Partial photo failures do
        // not change that boundary: the question itself already exists.
        if (
          !retireFmpHistoryDraft(
            window.history,
            getDraftSessionStorage(),
            widgetApi.getStoreHash(),
            draftNonceRef.current
          )
        ) {
          logger.warn('HISTORY_DRAFT_RETIREMENT_FAILED', { boundary: 'submission' });
        }
        draftNonceRef.current = null;

        const { questionId, slug, uploadCapability, manageCapability } = questionResponse.data;
        // ADR-024 §11: the opaque upload-proof handle, held in memory only (ref,
        // off the render surface). Read on the immediate uploads and on retry.
        uploadProofRef.current = uploadCapability?.token;
        // ADR-024 §10.4: the opaque MANAGE handle for the success-screen
        // "manage your photos" link. In memory only; never logged. Carried RAW
        // through the partial-failure state; the "show the CTA" decision is made at
        // each TERMINAL success on the count of SUCCESSFULLY-ATTACHED photos (not
        // merely selected) — a submission whose photos all failed has nothing to
        // manage, so no CTA (round-6 L7; round-7: gate on attached, not selected).
        const manageTokenRaw = manageCapability?.token;

        // Step 2: Upload images (if any). Each image is uploaded independently;
        // a failure is collected (with the File retained for retry) rather than
        // aborting — but a partial failure must NOT read as success
        // (BL-WIDGET-UPLOAD-FAILURE-UX-001).
        const failed: FailedImageUpload[] = [];

        if (images.length > 0) {
          for (let i = 0; i < images.length; i++) {
            // Progress: current = 2, 3, 4... (question was step 1)
            setSubmitState({
              status: 'submitting',
              step: 'images',
              current: i + 2,
              total: totalSteps,
            });

            const result = await uploadOneImage(
              widgetApi.images.upload,
              questionId,
              images[i],
              uploadProofRef.current
            );
            if (!result.ok) {
              failed.push({ file: images[i], name: images[i].name, message: result.message });
              logger.error('IMAGE_UPLOAD_FAILURE', null, {
                imageIndex: i + 1,
                fileName: images[i].name,
                errorMessage: result.message,
              });
              // Continue with remaining images
            }
          }
        }

        // The manage CTA is surfaced only when ≥1 photo actually ATTACHED.
        const attachedCount = images.length - failed.length;
        if (failed.length > 0) {
          // Partial failure: the question is real, but photos were dropped.
          // Do NOT show the success screen and do NOT call onSuccess — surface a
          // recoverable state so the customer knows their photos were not saved.
          // Carry the RAW handle (not a pre-gated value): a later retry can still
          // attach photos, at which point the terminal success re-evaluates the gate.
          setSubmitState({
            status: 'partial',
            questionId,
            slug,
            totalImages: images.length,
            failed,
            manageToken: manageTokenRaw,
            message: buildPartialUploadMessage('question', failed.length, images.length),
          });
        } else {
          // All photos attached (or none) — the proof is spent; drop it.
          uploadProofRef.current = undefined;
          setSubmitState({
            status: 'success',
            questionId,
            message: PENDING_REVIEW_MESSAGE,
            manageToken: attachedCount > 0 ? manageTokenRaw : undefined,
          });
          onSuccessArgs = { questionId, slug };
        }
      } catch (error) {
        // Network/client failures can bypass the response path above. Fail
        // closed so a re-rendered unchecked challenge never coexists with an
        // enabled Submit button backed by a stale or possibly consumed token.
        turnstileTokenRef.current = null;
        setTurnstileToken(null);
        const errorMessage =
          error instanceof Error ? error.message : 'An unexpected error occurred';
        setSubmitState({
          status: 'error',
          message: errorMessage,
        });
        notifyHostCallback(() =>
          onError?.(error instanceof Error ? error : new Error(errorMessage))
        );
      } finally {
        // Terminal (success/partial/error): allow a fresh submit again.
        submitInFlightRef.current = false;
      }

      // Outside the try/finally: a host onSuccess throw must not flip a created
      // question to `error`, nor escape as an unhandled rejection out of this
      // fire-and-forget onSubmit handler. Null unless the question was created
      // with every photo attached.
      if (onSuccessArgs) {
        const { questionId, slug } = onSuccessArgs;
        notifyHostCallback(() => onSuccess?.(questionId, slug));
      }
    },
    [formData, images, isOnline, onSuccess, onError]
  );

  /**
   * Retry only the images that failed, against the already-created question.
   * Never re-submits the question and never requires a fresh Turnstile token
   * (BL-WIDGET-UPLOAD-FAILURE-UX-001).
   */
  const handleRetryFailedImages = useCallback(async () => {
    // Synchronous guard (ref, not the async `isRetrying` state) so a same-tick
    // double-dispatch can't start two capability-use-spending retry sequences.
    if (submitState.status !== 'partial' || retryInFlightRef.current) return;
    const { questionId, slug, totalImages, failed, manageToken } = submitState;

    retryInFlightRef.current = true;
    setIsRetrying(true);
    let succeeded = false;
    try {
      // Re-use the same upload-proof handle (ADR-024 §11), read from the ref. If it
      // has expired (single-digit-minute lifetime), each upload fails gracefully
      // into the still-failed list rather than silently — the customer can resubmit.
      const stillFailed = await runImageUploadSequence(
        widgetApi.images.upload,
        questionId,
        failed.map((item) => item.file),
        uploadProofRef.current
      );

      if (stillFailed.length === 0) {
        // Every photo attached — the proof is spent; drop it.
        uploadProofRef.current = undefined;
        setSubmitState({
          status: 'success',
          questionId,
          message: PENDING_REVIEW_MESSAGE,
          manageToken,
        });
        succeeded = true;
      } else {
        setSubmitState({
          status: 'partial',
          questionId,
          slug,
          totalImages,
          failed: stillFailed,
          manageToken,
          message: buildPartialUploadMessage('question', stillFailed.length, totalImages),
        });
      }
    } finally {
      setIsRetrying(false);
      retryInFlightRef.current = false;
    }
    // Outside the try/finally: a throwing host onSuccess can't wedge the guard
    // (notifyHostCallback also keeps it from escaping as an unhandled rejection).
    if (succeeded) notifyHostCallback(() => onSuccess?.(questionId, slug));
  }, [submitState, onSuccess]);

  /**
   * Accept the submission without the photos that failed. The question is real
   * and pending review; this fires `onSuccess` since the user has chosen to
   * proceed (BL-WIDGET-UPLOAD-FAILURE-UX-001).
   */
  const handleContinueWithoutPhotos = useCallback(() => {
    if (submitState.status !== 'partial') return;
    const { questionId, slug, totalImages, failed, manageToken: manageTokenRaw } = submitState;
    // The user is giving up on the still-failed photos. Surface the manage CTA only
    // if at least one photo DID attach; if every photo failed there is nothing to
    // manage (round-7: gate on attached, not selected).
    const attachedCount = totalImages - failed.length;
    // Terminal: the user is done with photos — the proof is no longer needed.
    uploadProofRef.current = undefined;
    setSubmitState({
      status: 'success',
      questionId,
      message: PENDING_REVIEW_MESSAGE,
      manageToken: attachedCount > 0 ? manageTokenRaw : undefined,
    });
    notifyHostCallback(() => onSuccess?.(questionId, slug));
  }, [submitState, onSuccess]);

  /**
   * Reset form to initial state.
   */
  const handleReset = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      posterName: '',
      posterEmail: '',
      windowType: '', // P1-29: Reset window type
      consentToPublicDisplay: false, // BL-PRIVACY-CONSENT-CAPTURE-001
    });
    setImages([]);
    setIsPhotoGuidanceVisible(true);
    setPhotoGuidanceStatus('');
    setErrors({});
    setSubmitState({ status: 'idle' });
    turnstileTokenRef.current = null;
    setTurnstileToken(null);
    uploadProofRef.current = undefined;
    submitInFlightRef.current = false;
    if (
      !retireFmpHistoryDraft(
        window.history,
        getDraftSessionStorage(),
        widgetApi.getStoreHash(),
        draftNonceRef.current
      )
    ) {
      logger.warn('HISTORY_DRAFT_RETIREMENT_FAILED', { boundary: 'reset' });
    }
    draftNonceRef.current = null;
    // Reset Turnstile widget for fresh verification
    turnstileRef.current?.reset();
  }, []);

  const isSubmitting = submitState.status === 'submitting';
  const isSuccess = submitState.status === 'success';

  // Render success state
  if (isSuccess) {
    return (
      <div className={`fmp-form fmp-form--success ${className}`}>
        <div
          ref={successRef}
          className="fmp-success"
          role="status"
          aria-live="polite"
          tabIndex={-1}
        >
          <svg
            className="fmp-success__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9 12l2 2 4-4" />
          </svg>
          <h2 className="fmp-success__title">Question Submitted!</h2>
          <p className="fmp-success__message">{submitState.message}</p>
          {submitState.manageToken ? (
            <>
              <p className="fmp-success__message">
                Uploaded photos? Save your private link below — no account needed — to view or
                remove them any time.
              </p>
              {/* ADR-024 §10.4 — the cookieless manage/self-delete magic-link.
                  The token is placed ONLY in this href (the intended URL); it is
                  never logged. rel="noreferrer" keeps the storefront URL out of
                  the Referer on navigation. */}
              <a
                href={buildManageUrl(submitState.manageToken)}
                target="_blank"
                rel="noopener noreferrer"
                className="fmp-btn fmp-btn--primary"
              >
                Manage your photos
              </a>
            </>
          ) : null}
          <button type="button" onClick={handleReset} className="fmp-btn fmp-btn--secondary">
            Submit Another Question
          </button>
        </div>
      </div>
    );
  }

  // Render partial-failure state (question created, some/all photos failed).
  // BL-WIDGET-UPLOAD-FAILURE-UX-001: this is NOT the success screen — it is a
  // recoverable, assertively-announced state with retry / continue actions.
  if (submitState.status === 'partial') {
    return (
      <ImageUploadFailureNotice
        message={submitState.message}
        failed={submitState.failed}
        onRetry={handleRetryFailedImages}
        onContinue={handleContinueWithoutPhotos}
        isRetrying={isRetrying}
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`fmp-form ${className}`}
      noValidate
      aria-describedby={errors.general ? `${formId}-error` : undefined}
    >
      <div className="fmp-form__intro">
        <h2 className="fmp-form__title">Ask a Question</h2>
        <p className="fmp-form__description">
          Tell us where the part is used and what it connects to. Clear details and optional photos
          help our team identify it faster.
        </p>
      </div>

      {/* Offline Notice */}
      {!isOnline && <OfflineNotice />}

      {/* General Error Alert */}
      {errors.general && (
        <div
          id={`${formId}-error`}
          role="alert"
          className="fmp-alert fmp-alert--error"
          aria-live="polite"
        >
          {errors.general}
        </div>
      )}

      {/* Submission Error Alert */}
      {submitState.status === 'error' && (
        <div role="alert" className="fmp-alert fmp-alert--error" aria-live="polite">
          {submitState.message}
        </div>
      )}

      {/* === Section 1: Your Question === */}
      <fieldset className="fmp-section fmp-section--question">
        <legend className="fmp-section__legend">Your Question</legend>
        <div className="fmp-section__body">
          {/* Title Field */}
          <div className="fmp-field fmp-field--title">
            <label htmlFor={titleId} className="fmp-field__label">
              Question Title{' '}
              <span className="fmp-field__required" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id={titleId}
              name="title"
              type="text"
              value={formData.title}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`fmp-field__input ${errors.title ? 'fmp-field__input--error' : ''}`}
              placeholder="e.g., Can't identify my sliding door roller"
              aria-required="true"
              aria-invalid={!!errors.title}
              aria-describedby={errors.title ? `${titleId}-error` : undefined}
              maxLength={CONTENT_VALIDATION.MAX_TITLE_LENGTH}
            />
            <div className="fmp-field__meta">
              <CharacterCount
                current={formData.title.length}
                min={CONTENT_VALIDATION.MIN_TITLE_LENGTH}
                max={CONTENT_VALIDATION.MAX_TITLE_LENGTH}
                id={`${titleId}-count`}
              />
            </div>
            {errors.title && (
              <p id={`${titleId}-error`} className="fmp-field__error" role="alert">
                {errors.title}
              </p>
            )}
            {/* Similar Questions (P1-19) */}
            <SimilarQuestions
              questions={similarQuestions}
              isLoading={isSimilarLoading}
              show={formData.title.trim().length >= 3}
              hasSelectedPhotos={images.length > 0}
              onBeforeSameTabNavigate={persistDraftBeforeNavigate}
            />
          </div>

          {/* Description Field */}
          <div className="fmp-field fmp-field--description">
            <label htmlFor={descriptionId} className="fmp-field__label">
              Description{' '}
              <span className="fmp-field__required" aria-hidden="true">
                *
              </span>
            </label>
            <textarea
              id={descriptionId}
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`fmp-field__textarea ${errors.description ? 'fmp-field__textarea--error' : ''}`}
              placeholder="Describe the part you need help with. Include dimensions, color, material, or any identifying marks..."
              rows={5}
              aria-required="true"
              aria-invalid={!!errors.description}
              aria-describedby={errors.description ? `${descriptionId}-error` : undefined}
              maxLength={CONTENT_VALIDATION.MAX_DESCRIPTION_LENGTH}
            />
            <div className="fmp-field__meta">
              <CharacterCount
                current={formData.description.length}
                min={CONTENT_VALIDATION.MIN_DESCRIPTION_LENGTH}
                max={CONTENT_VALIDATION.MAX_DESCRIPTION_LENGTH}
                id={`${descriptionId}-count`}
              />
            </div>
            {errors.description && (
              <p id={`${descriptionId}-error`} className="fmp-field__error" role="alert">
                {errors.description}
              </p>
            )}
            <p className="fmp-field__hint">Be as detailed as possible.</p>
          </div>

          {/* Window Type Selector (P1-29) */}
          <div className="fmp-field fmp-field--window-type">
            <label htmlFor={windowTypeId} className="fmp-field__label">
              Window Type <span className="fmp-field__optional">(optional)</span>
            </label>
            <select
              id={windowTypeId}
              name="windowType"
              value={formData.windowType}
              onChange={handleChange}
              disabled={isSubmitting}
              className="fmp-field__select"
            >
              <option value="">Select window type (if known)</option>
              {WINDOW_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="fmp-field__hint">Helps us identify the correct part faster.</p>
          </div>
        </div>
      </fieldset>

      {/* === Section 2: Photos === */}
      <fieldset className="fmp-section fmp-section--photos">
        <legend className="fmp-section__legend">Photos</legend>
        <div className="fmp-section__body">
          <div className="fmp-field" role="group" aria-labelledby={imagesLabelId}>
            <span id={imagesLabelId} className="fmp-field__label">
              Attach Images <span className="fmp-field__optional">(optional)</span>
            </span>
            <div className="fmp-photo-guidance" role="note" aria-labelledby={photoGuidanceTitleId}>
              <div className="fmp-photo-guidance__header">
                <h3 id={photoGuidanceTitleId} className="fmp-photo-guidance__title">
                  Photo tips
                </h3>
                <button
                  type="button"
                  className="fmp-photo-guidance__toggle"
                  aria-expanded={isPhotoGuidanceVisible}
                  aria-controls={photoGuidanceContentId}
                  onClick={
                    isPhotoGuidanceVisible ? handleDismissPhotoGuidance : handleShowPhotoGuidance
                  }
                >
                  {isPhotoGuidanceVisible ? 'Hide tips' : 'Show photo tips'}
                </button>
              </div>
              <div id={photoGuidanceContentId} hidden={!isPhotoGuidanceVisible}>
                <ul className="fmp-photo-guidance__list">
                  <li>Start with the whole part and where it fits.</li>
                  <li>Add a close-up of numbers, stamps, damage, or connectors.</li>
                  <li>Use good light and include a ruler when size is unclear.</li>
                </ul>
              </div>
            </div>
            <p
              className="fmp-photo-guidance__status"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {photoGuidanceStatus}
            </p>
            <ImageUploader
              images={images}
              onImagesChange={setImages}
              disabled={isSubmitting}
              enableCameraCapture
            />
            <p className="fmp-field__hint">
              Add up to 5 images. JPG, PNG, WebP, HEIC, and HEIF are accepted.
            </p>
          </div>
        </div>
      </fieldset>

      {/* === Section 3: Your Details === */}
      <fieldset className="fmp-section fmp-section--details">
        <legend className="fmp-section__legend">Your Details</legend>
        <div className="fmp-section__body">
          <div className="fmp-details__contact">
            {/* Poster Name Field */}
            <div className="fmp-field fmp-field--name">
              <label htmlFor={nameId} className="fmp-field__label">
                Your Name{' '}
                <span className="fmp-field__required" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id={nameId}
                name="posterName"
                type="text"
                value={formData.posterName}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`fmp-field__input ${errors.posterName ? 'fmp-field__input--error' : ''}`}
                placeholder="Enter your name"
                aria-required="true"
                aria-invalid={!!errors.posterName}
                aria-describedby={errors.posterName ? `${nameId}-error` : undefined}
                maxLength={100}
              />
              {errors.posterName && (
                <p id={`${nameId}-error`} className="fmp-field__error" role="alert">
                  {errors.posterName}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="fmp-field fmp-field--email">
              <label htmlFor={emailId} className="fmp-field__label">
                Email Address{' '}
                <span className="fmp-field__required" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id={emailId}
                name="posterEmail"
                type="email"
                value={formData.posterEmail}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`fmp-field__input ${errors.posterEmail ? 'fmp-field__input--error' : ''}`}
                placeholder="you@example.com"
                aria-required="true"
                aria-invalid={!!errors.posterEmail}
                aria-describedby={errors.posterEmail ? `${emailId}-error` : undefined}
              />
              {errors.posterEmail && (
                <p id={`${emailId}-error`} className="fmp-field__error" role="alert">
                  {errors.posterEmail}
                </p>
              )}
              <p className="fmp-field__hint">
                We&apos;ll notify you when your question is answered.
              </p>
            </div>
          </div>

          {/* Turnstile Verification */}
          <div
            ref={turnstileGroupRef}
            className="fmp-field fmp-field--turnstile"
            role="group"
            tabIndex={-1}
            aria-labelledby={turnstileLabelId}
            aria-describedby={errors.turnstile ? turnstileErrorId : undefined}
          >
            <span id={turnstileLabelId} className="fmp-field__label">
              Security Verification{' '}
              <span className="fmp-field__required" aria-hidden="true">
                *
              </span>
            </span>
            <div
              ref={turnstileSlotRef}
              className="fmp-turnstile-slot"
              data-fmp-turnstile-size={turnstileSize}
            >
              {turnstileLoading ? (
                <div className="fmp-turnstile-placeholder">Loading verification...</div>
              ) : turnstileSiteKey ? (
                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  onVerify={handleTurnstileVerify}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpire}
                  onLoad={handleTurnstileLoad}
                  disabled={isSubmitting}
                  size={turnstileSize}
                  reinitializeOnVerificationError
                />
              ) : (
                <div className="fmp-turnstile-placeholder fmp-turnstile-placeholder--error">
                  Verification unavailable
                </div>
              )}
            </div>
            {errors.turnstile && (
              <p id={turnstileErrorId} className="fmp-field__error" role="alert">
                {errors.turnstile}
              </p>
            )}
          </div>
        </div>
      </fieldset>

      {/* === Section 4: Publication Consent === */}
      <fieldset className="fmp-section fmp-section--consent">
        <legend className="fmp-section__legend">Publication Consent</legend>
        <div className="fmp-section__body">
          {/* Public-display consent (BL-PRIVACY-CONSENT-CAPTURE-001). Keep the
              governed field, copy, validation, and unchecked default distinct
              from contact details, CAPTCHA, and optional photo guidance. */}
          <div className="fmp-field fmp-field--consent">
            <label htmlFor={consentId} className="fmp-consent">
              <input
                id={consentId}
                name="consentToPublicDisplay"
                type="checkbox"
                checked={formData.consentToPublicDisplay}
                onChange={handleConsentChange}
                disabled={isSubmitting}
                className="fmp-consent__checkbox"
                aria-required="true"
                aria-invalid={!!errors.consentToPublicDisplay}
                aria-describedby={errors.consentToPublicDisplay ? `${consentId}-error` : undefined}
              />
              <span className="fmp-consent__text">
                I agree that my question, the details I provide, and any photos I upload may be
                published publicly on this store&apos;s website to help other customers.{' '}
                <span className="fmp-field__required" aria-hidden="true">
                  *
                </span>
              </span>
            </label>
            {errors.consentToPublicDisplay && (
              <p id={`${consentId}-error`} className="fmp-field__error" role="alert">
                {errors.consentToPublicDisplay}
              </p>
            )}
          </div>
        </div>
      </fieldset>

      {/* Submit Button */}
      <div className="fmp-form__actions">
        <button
          type="submit"
          disabled={isSubmitting || !isOnline || !turnstileToken}
          className="fmp-btn fmp-btn--primary"
        >
          {isSubmitting ? (
            <>
              <span className="fmp-btn__spinner" aria-hidden="true" />
              {submitState.step === 'question'
                ? 'Submitting...'
                : `Uploading image ${submitState.current - 1} of ${submitState.total - 1}...`}
            </>
          ) : (
            'Submit Question'
          )}
        </button>
      </div>

      {/* Progress Indicator (during submission) */}
      {/* P1-15: Use indeterminate spinner for no-image submissions */}
      {isSubmitting && submitState.step === 'question' && images.length === 0 && (
        <div
          className="fmp-progress fmp-progress--indeterminate"
          role="progressbar"
          aria-busy="true"
          aria-label="Submitting question"
        >
          <div className="fmp-progress__bar fmp-progress__bar--indeterminate" />
        </div>
      )}
      {/* Determinate progress bar for submissions with images */}
      {isSubmitting && (submitState.step === 'images' || images.length > 0) && (
        <div
          className="fmp-progress"
          role="progressbar"
          aria-valuenow={submitState.current}
          aria-valuemin={0}
          aria-valuemax={submitState.total}
          aria-label="Submission progress"
        >
          <div
            className="fmp-progress__bar"
            style={{
              width: `${(submitState.current / submitState.total) * 100}%`,
            }}
          />
        </div>
      )}
    </form>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const formStyles = `
  :where(.fmp-widget-scope) .fmp-form {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-lg);
    padding-block: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-form__intro {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-sm);
    max-width: var(--fmp-layout-prose-max);
  }

  :where(.fmp-widget-scope) .fmp-form__title {
    margin: 0;
    font-size: var(--fmp-font-size-xl);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-form__description {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    line-height: var(--fmp-line-height-body);
  }

  /* Form section grouping */
  :where(.fmp-widget-scope) .fmp-section {
    min-inline-size: 0;
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-surface);
    margin: 0;
    padding: var(--fmp-space-lg);
    background-color: var(--fmp-color-surface);
  }

  :where(.fmp-widget-scope) .fmp-section__legend {
    display: block;
    float: none;
    inline-size: auto;
    max-inline-size: 100%;
    margin: 0;
    padding: 0 var(--fmp-space-sm);
    border: 0;
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    line-height: var(--fmp-line-height-heading);
    color: var(--fmp-color-text);
    white-space: normal;
  }

  :where(.fmp-widget-scope) .fmp-section__body {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-xl);
    inline-size: 100%;
    max-inline-size: none;
  }

  :where(.fmp-widget-scope) .fmp-details__contact {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-xl);
  }

  :where(.fmp-widget-scope) .fmp-form__actions {
    margin-top: var(--fmp-space-sm);
    max-width: var(--fmp-layout-prose-max);
  }

  :where(.fmp-widget-scope) .fmp-field {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-size-6);
  }

  :where(.fmp-widget-scope) .fmp-field__label {
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-field__required {
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-field__optional {
    font-weight: var(--fmp-font-weight-regular);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-field__input,
  :where(.fmp-widget-scope) .fmp-field__textarea {
    min-height: var(--fmp-size-44);
    padding: var(--fmp-size-10) var(--fmp-space-md);
    border: var(--fmp-border-width) solid var(--fmp-color-border-light);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    font-family: inherit;
    transition: box-shadow var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-field__input:focus-visible,
  :where(.fmp-widget-scope) .fmp-field__textarea:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-field__input:disabled,
  :where(.fmp-widget-scope) .fmp-field__textarea:disabled {
    background-color: var(--fmp-color-surface-muted);
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-field__input--error,
  :where(.fmp-widget-scope) .fmp-field__textarea--error {
    border-color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-field__input--error:focus-visible,
  :where(.fmp-widget-scope) .fmp-field__textarea--error:focus-visible {
    box-shadow: var(--fmp-shadow-focus-danger);
  }

  :where(.fmp-widget-scope) .fmp-field__textarea {
    resize: vertical;
    min-height: var(--fmp-size-100);
  }

  /* P1-29: Select dropdown styles */
  :where(.fmp-widget-scope) .fmp-field__select {
    min-height: var(--fmp-size-44);
    padding: var(--fmp-size-10) var(--fmp-size-36) var(--fmp-size-10) var(--fmp-space-md);
    border: var(--fmp-border-width) solid var(--fmp-color-border-light);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    font-family: inherit;
    background-color: var(--fmp-color-surface);
    cursor: pointer;
    transition: box-shadow var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-field__select:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-field__select:disabled {
    background-color: var(--fmp-color-surface-muted);
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-field__error {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0;
    font-size: var(--fmp-font-size-sm);
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-field__hint {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0;
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
  }

  /* Public-display consent (BL-PRIVACY-CONSENT-CAPTURE-001) */
  :where(.fmp-widget-scope) .fmp-field--consent {
    padding: var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-info-border);
    border-radius: var(--fmp-radius-control);
    background-color: var(--fmp-color-info-bg);
  }

  :where(.fmp-widget-scope) .fmp-consent {
    display: flex;
    align-items: flex-start;
    gap: var(--fmp-size-10);
    min-height: var(--fmp-size-44);
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-consent__checkbox {
    margin-top: var(--fmp-size-2);
    width: var(--fmp-size-20);
    height: var(--fmp-size-20);
    flex-shrink: 0;
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-consent__checkbox:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-consent__text {
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-body);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-alert {
    padding: var(--fmp-space-md) var(--fmp-space-lg);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
  }

  :where(.fmp-widget-scope) .fmp-alert--error {
    background-color: var(--fmp-color-error-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-error-border);
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--fmp-space-sm);
    padding: var(--fmp-space-md) var(--fmp-space-2xl);
    border: none;
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    font-family: inherit;
    cursor: pointer;
    text-decoration: none;
    min-height: var(--fmp-size-44);
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard), opacity var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-btn--primary {
    background-color: var(--fmp-color-primary);
    color: var(--fmp-color-text-on-primary);
  }

  :where(.fmp-widget-scope) .fmp-btn--primary:hover:not(:disabled) {
    background-color: var(--fmp-color-primary-hover);
  }

  :where(.fmp-widget-scope) .fmp-btn--secondary {
    background-color: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-btn--secondary:hover:not(:disabled) {
    background-color: var(--fmp-color-surface-hover);
  }

  :where(.fmp-widget-scope) .fmp-btn__spinner {
    width: var(--fmp-size-16);
    height: var(--fmp-size-16);
    border: var(--fmp-border-width-strong) solid var(--fmp-color-overlay-control-hover);
    border-top-color: var(--fmp-color-text-on-primary);
    border-radius: 50%;
    animation: fmp-spin var(--fmp-duration-spin) linear infinite;
  }

  @keyframes fmp-spin {
    to {
      transform: rotate(360deg);
    }
  }

  :where(.fmp-widget-scope) .fmp-progress {
    height: var(--fmp-size-4);
    background-color: var(--fmp-color-progress-track);
    border-radius: var(--fmp-radius-sm);
    overflow: hidden;
  }

  :where(.fmp-widget-scope) .fmp-progress__bar {
    height: 100%;
    background-color: var(--fmp-color-primary);
    transition: none;
  }

  /* P1-15: Indeterminate progress bar for no-image submissions */
  :where(.fmp-widget-scope) .fmp-progress--indeterminate {
    position: relative;
  }

  :where(.fmp-widget-scope) .fmp-progress__bar--indeterminate {
    position: absolute;
    width: 30%;
    animation: fmp-indeterminate var(--fmp-duration-shimmer) ease-in-out infinite;
  }

  @keyframes fmp-indeterminate {
    0% {
      left: -30%;
    }
    100% {
      left: 100%;
    }
  }

  :where(.fmp-widget-scope) .fmp-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--fmp-size-40) var(--fmp-space-xl);
    gap: var(--fmp-space-lg);
    max-width: var(--fmp-layout-prose-max);
    margin: 0 auto;
    border: var(--fmp-border-width) solid var(--fmp-color-success-border);
    border-radius: var(--fmp-radius-surface);
    background-color: var(--fmp-color-success-bg);
  }

  :where(.fmp-widget-scope) .fmp-success:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-success__icon {
    width: var(--fmp-size-64);
    height: var(--fmp-size-64);
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-success__title {
    margin: 0;
    font-size: var(--fmp-font-size-xl);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-success__message {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-form--success {
    padding-block: var(--fmp-space-2xl);
  }

  /* Turnstile Widget Styles */
  :where(.fmp-widget-scope) .fmp-turnstile {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-turnstile__loading {
    padding: var(--fmp-space-md);
    background-color: var(--fmp-color-surface-muted);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    text-align: center;
  }

  :where(.fmp-widget-scope) .fmp-turnstile__error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--fmp-space-md);
    padding: var(--fmp-space-md);
    background-color: var(--fmp-color-error-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-error-border);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-turnstile__retry {
    padding: var(--fmp-size-6) var(--fmp-space-md);
    border: var(--fmp-border-width) solid var(--fmp-color-error);
    border-radius: var(--fmp-radius-sm);
    background: transparent;
    color: var(--fmp-color-error);
    font-size: var(--fmp-font-size-sm);
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-turnstile__retry:hover {
    background-color: var(--fmp-color-error-bg);
  }

  :where(.fmp-widget-scope) .fmp-turnstile__container {
    min-height: var(--fmp-size-65);
  }

  :where(.fmp-widget-scope) .fmp-turnstile-placeholder {
    padding: var(--fmp-space-md);
    background-color: var(--fmp-color-surface-muted);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    text-align: center;
  }

  :where(.fmp-widget-scope) .fmp-turnstile-placeholder--error {
    background-color: var(--fmp-color-error-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-error-border);
    color: var(--fmp-color-error);
  }

  /* Cloudflare compact is 150x140px; flexible is 100% x 65px with a 300px
   * minimum. Reserve the resolved Ask-only size through config, script, ready,
   * error and disabled phases so lower controls do not shift. */
  :where(.fmp-widget-scope) .fmp-turnstile-slot,
  :where(.fmp-widget-scope) .fmp-turnstile-slot > .fmp-turnstile,
  :where(.fmp-widget-scope) .fmp-turnstile-slot > .fmp-turnstile-placeholder {
    box-sizing: border-box;
    inline-size: 100%;
    min-inline-size: 0;
    margin: 0;
    padding: 0;
    border: 0;
    min-block-size: var(--fmp-size-160);
  }

  :where(.fmp-widget-scope) .fmp-turnstile-slot[data-fmp-turnstile-size='flexible'],
  :where(.fmp-widget-scope) .fmp-turnstile-slot[data-fmp-turnstile-size='flexible'] > .fmp-turnstile,
  :where(.fmp-widget-scope) .fmp-turnstile-slot[data-fmp-turnstile-size='flexible'] > .fmp-turnstile-placeholder {
    min-block-size: var(--fmp-size-65);
  }

  :where(.fmp-widget-scope) .fmp-turnstile-slot > .fmp-turnstile-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :where(.fmp-widget-scope) .fmp-field--turnstile:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  /* Character Count Styles (P1-9) */
  :where(.fmp-widget-scope) .fmp-field__meta {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--fmp-space-xs);
  }

  :where(.fmp-widget-scope) .fmp-char-count {
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
    font-variant-numeric: tabular-nums;
  }

  :where(.fmp-widget-scope) .fmp-char-count--warning {
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-char-count--error {
    color: var(--fmp-color-error);
    font-weight: var(--fmp-font-weight-semibold);
  }

  /* Ephemeral, non-modal photo guidance (ADR-028 Slice 2A). */
  :where(.fmp-widget-scope) .fmp-photo-guidance {
    max-width: var(--fmp-layout-prose-max);
    padding: var(--fmp-space-lg);
    background: var(--fmp-color-info-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-info-border);
    border-radius: var(--fmp-radius-surface);
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__title {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__list {
    margin: var(--fmp-space-sm) 0 0;
    padding-left: var(--fmp-space-xl);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    line-height: var(--fmp-line-height-body);
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__list li + li {
    margin-top: var(--fmp-space-xs);
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__toggle {
    min-height: var(--fmp-size-44);
    padding: var(--fmp-space-sm) var(--fmp-space-md);
    border: var(--fmp-border-width) solid var(--fmp-color-primary);
    border-radius: var(--fmp-radius-control);
    background-color: var(--fmp-color-surface);
    color: var(--fmp-color-primary);
    font: inherit;
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__toggle:hover {
    background-color: var(--fmp-color-surface-hover);
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__toggle:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-photo-guidance__status {
    min-height: var(--fmp-size-24);
    max-width: var(--fmp-layout-prose-max);
    margin: 0;
    color: var(--fmp-color-text-muted);
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-body);
  }

  /* Base rules are the robust no-container-query, mobile-first fallback. */
  :where(.fmp-widget-scope) .fmp-form__actions .fmp-btn {
    width: 100%;
  }

  @container fmp-ask (min-width: ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-form {
      padding-block: var(--fmp-space-xl);
      gap: var(--fmp-space-xl);
    }

    :where(.fmp-widget-scope) .fmp-section {
      padding: var(--fmp-space-xl);
    }

    :where(.fmp-widget-scope) .fmp-form__actions .fmp-btn {
      width: auto;
    }

    :where(.fmp-widget-scope) .fmp-photo-guidance__header {
      align-items: flex-start;
    }
  }

  @container fmp-ask (min-width: ${BREAKPOINTS.container.wide}) {
    :where(.fmp-widget-scope) .fmp-form {
      padding-block: var(--fmp-space-section);
    }

    :where(.fmp-widget-scope) .fmp-details__contact {
      flex-direction: row;
    }

    :where(.fmp-widget-scope) .fmp-details__contact .fmp-field {
      flex: 1;
      min-width: 0;
    }
  }

  /* High contrast mode support for accessibility */
  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-field__input:focus-visible,
    :where(.fmp-widget-scope) .fmp-field__textarea:focus-visible,
    :where(.fmp-widget-scope) .fmp-field__select:focus-visible {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) currentColor;
      outline-offset: var(--fmp-outline-offset);
      box-shadow: none;
    }

    :where(.fmp-widget-scope) .fmp-btn:focus-visible {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) currentColor;
      outline-offset: var(--fmp-outline-offset);
    }

    :where(.fmp-widget-scope) .fmp-photo-guidance__toggle:focus-visible,
    :where(.fmp-widget-scope) .fmp-consent__checkbox:focus-visible,
    :where(.fmp-widget-scope) .fmp-field--turnstile:focus-visible,
    :where(.fmp-widget-scope) .fmp-success:focus-visible {
      outline-color: currentColor;
      box-shadow: none;
    }
  }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-btn__spinner {
      animation: none;
    }

    :where(.fmp-widget-scope) .fmp-progress__bar {
      transition: none;
    }

    :where(.fmp-widget-scope) .fmp-progress__bar--indeterminate {
      animation: none;
      left: 0;
      width: 100%;
    }
  }
`;
